import AddCircleIcon from '@mui/icons-material/AddCircle';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormLabel,
  InputBaseComponentProps,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchShippingLines, ShippingLineQueryKeys } from 'services/clients/shippingLine';
import { ShippingLine } from 'types/Shipping';

interface Props {
  bolInputProps: InputBaseComponentProps;
  onChangeCarrier: (scac: string, shippingLine: string) => void;
  isValid: boolean;
}

const ShippingLineOption: React.FC<{ ssl: ShippingLine }> = ({ ssl }) => {
  const comingSoon = ssl.scacs.length === 0 || ssl.bolTrackingEnabled === false;
  return (
    <MenuItem key={ssl.key} value={ssl.key} disabled={comingSoon}>
      <span>
        {ssl.name}
        {comingSoon && <span> - Coming soon</span>}
      </span>
    </MenuItem>
  );
};

export const Line: React.FC<Props> = (props) => {
  const { onChangeCarrier, isValid, bolInputProps } = props;
  const [state, setState] = React.useState<{
    carrierKey: string;
    carrierScac: string;
    locked: boolean;
    hasScac: boolean;
  }>({
    carrierKey: '',
    carrierScac: '',
    locked: false,
    hasScac: false,
  });

  const carriers = useQuery([ShippingLineQueryKeys.ShippingLines, 'all'], ({ signal }) =>
    fetchShippingLines({ signal })
  );

  const checkScac = (value: string) => {
    const match = value.toUpperCase().slice(0, 4);
    if (match.match(/^[A-Z]+$/)) {
      const carrier = (carriers.data || []).find((c) => c.scacs.includes(match));
      if (carrier) {
        onChangeCarrier(match, carrier.key);
      }
      setState((s) => ({
        ...s,
        carrierKey: carrier ? carrier.key : s.carrierKey,
        carrierScac: match,
        locked: !!carrier,
        hasScac: true,
      }));
    } else {
      setState((s) => ({ ...s, carrierScac: '', locked: false, hasScac: false }));
    }
    return true;
  };

  return (
    <div className="flex space-x-4 items-end">
      <FormControl className="flex-grow">
        <FormLabel>
          <h4 className="text-gray-900">Ocean BOL</h4>
        </FormLabel>
        <OutlinedInput
          size="small"
          placeholder="Enter your Ocean BOL"
          disabled={carriers.isLoading}
          onChange={(e) => {
            checkScac(e.target.value);
          }}
          inputProps={bolInputProps}
        />
      </FormControl>
      <FormControl className="flex-grow">
        <FormLabel>
          <h4 className="text-gray-900">Steamship Line</h4>
        </FormLabel>
        <Select
          size="small"
          className="h-[39px]"
          disabled={carriers.isLoading || state.locked}
          displayEmpty
          renderValue={(key: string) => {
            const carrier = carriers.data?.find((c) => c.key === key);
            if (carrier) {
              return <h4>{carrier.name}</h4>;
            }
            return <h4 className="text-gray-400">Steamship Line</h4>;
          }}
          value={state.carrierKey}
          onChange={(e) => {
            onChangeCarrier(state.carrierScac, e.target.value as string);
            setState((s) => ({ ...s, carrierKey: e.target.value as string }));
          }}
        >
          {(carriers.data || []).map((carrier) => ShippingLineOption({ ssl: carrier }))}
        </Select>
      </FormControl>
      <LoadingButton
        variant="contained"
        startIcon={<AddCircleIcon />}
        className="py-1 px-6"
        type="submit"
        disabled={!isValid}
        color="secondary"
        onClick={() => {
          setState((s) => ({
            ...s,
            carrierKey: '',
            carrierScac: '',
            locked: false,
            hasScac: false,
          }));
        }}
      >
        <h3>Add shipment</h3>
      </LoadingButton>
    </div>
  );
};
Line.displayName = 'Manual.AddForm.Line';
